import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'

/**
 * Itens que o quadro recorta mas nenhuma lane recebe — o status deles não está
 * mapeado em lane nenhuma (SPEC-20261006-1825-itens-nao-mapeados-em-lista).
 *
 * Antes era um botão com o texto inteiro ("161 itens não mapeados") que, ao
 * clicar, soltava um aviso com os códigos todos numa linha só: 161 códigos
 * enfileirados num toast, sem nome e sem como abrir nenhum.
 *
 * Agora o botão mostra só o ícone e a quantidade — o texto continua no `title`
 * e no rótulo acessível, porque um número sozinho não diz o que conta —, e o
 * clique abre a lista com código e nome. O código leva para a edição do item,
 * ao lado do explorer, e fechar volta para o quadro.
 */
export function ItensNaoMapeados({
  boardId,
  itens,
  onFiltrarNaLista,
}: {
  boardId: string
  itens: readonly { id: string; code: string; title: string }[]
  /**
   * Mostra estes itens na Lista do quadro, filtrada só por eles
   * (SPEC-20261006-2326-nao-mapeados-busca-e-filtro). Sem ele — quem só tem
   * acesso ao Kanban ou à Planilha —, o ícone não aparece.
   */
  onFiltrarNaLista?: () => void
}) {
  const navigate = useNavigate()
  const [aberta, setAberta] = useState(false)
  const botao = useRef<HTMLButtonElement>(null)
  const lista = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; right: number; maxHeight: number } | null>(null)
  const [busca, setBusca] = useState('')

  /*
    Busca por código ou nome, sem acento e sem caixa — as regras da busca do
    quadro (pedido do Cristian, 2026-10-06). Com dezenas de itens, achar um
    rolando a lista era o único caminho.
  */
  const semAcento = (texto: string) => texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
  const termo = semAcento(busca.trim())
  const achados = termo === '' ? itens : itens.filter((item) => semAcento(`${item.code} ${item.title}`).includes(termo))

  const n = itens.length
  const rotulo = `${n} ${n === 1 ? 'item não mapeado' : 'itens não mapeados'}`

  /**
   * A lista vai para o `body`, em posição fixa: o cabeçalho do quadro corta o
   * que transborda dele. A ALTURA é a da tela — do botão até a borda de baixo —,
   * e o que passar disso rola por dentro (pedido do Cristian, 2026-10-06).
   */
  useLayoutEffect(() => {
    if (!aberta) {
      // reabrir começa do zero: a busca de antes esconderia itens sem aviso
      setBusca('')
      return
    }
    const r = botao.current?.getBoundingClientRect()
    if (!r) return
    const margem = 12
    const top = r.bottom + 6
    setPos({
      top,
      right: Math.max(margem, window.innerWidth - r.right),
      maxHeight: Math.max(160, window.innerHeight - top - margem),
    })
  }, [aberta])

  /*
    Fecha ao clicar fora, no Esc e ao redimensionar — mas NÃO ao rolar: a lista
    rola por dentro, e fechar a cada rolagem a fecharia na primeira tentativa
    de descer por ela.
  */
  useEffect(() => {
    if (!aberta) return
    const fora = (e: PointerEvent) => {
      const alvo = e.target as Node
      if (lista.current?.contains(alvo) || botao.current?.contains(alvo)) return
      setAberta(false)
    }
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberta(false)
    }
    const fechar = () => setAberta(false)
    document.addEventListener('pointerdown', fora, true)
    window.addEventListener('keydown', tecla)
    window.addEventListener('resize', fechar)
    return () => {
      document.removeEventListener('pointerdown', fora, true)
      window.removeEventListener('keydown', tecla)
      window.removeEventListener('resize', fechar)
    }
  }, [aberta])

  if (n === 0) return null

  return (
    <>
      <button
        ref={botao}
        type="button"
        className={`kb__btn kb__btn--warn kb__naomapeados${aberta ? ' kb__naomapeados--on' : ''}`}
        onClick={() => setAberta((v) => !v)}
        aria-expanded={aberta}
        aria-haspopup="listbox"
        aria-label={rotulo}
        title={`${rotulo}: status fora das lanes deste quadro`}
      >
        <i className="fas fa-exclamation-triangle" aria-hidden />
        <span>{n}</span>
      </button>

      {aberta && pos
        ? createPortal(
            <div
              ref={lista}
              className="kb__naomapeados-lista"
              role="listbox"
              aria-label={rotulo}
              style={{ top: pos.top, right: pos.right, maxHeight: pos.maxHeight }}
            >
              <div className="kb__naomapeados-topo">
                <div className="kb__naomapeados-textos">
                  <strong>{rotulo}</strong>
                  <span>Status fora das lanes deste quadro</span>
                </div>
                {onFiltrarNaLista ? (
                  <button
                    type="button"
                    className="kb__naomapeados-filtrar"
                    aria-label="Ver só estes itens na Lista"
                    title="Ver só estes itens na Lista"
                    onClick={() => {
                      setAberta(false)
                      onFiltrarNaLista()
                    }}
                  >
                    <i className="fas fa-filter" aria-hidden />
                  </button>
                ) : null}
              </div>
              <div className={`kb__buscar kb__naomapeados-busca${busca.trim() ? ' kb__buscar--ativa' : ''}`}>
                <i className="fas fa-search" aria-hidden />
                <input
                  autoFocus
                  value={busca}
                  placeholder="Localizar item…"
                  aria-label="Localizar entre os itens não mapeados"
                  onChange={(e) => setBusca(e.target.value)}
                  onKeyDown={(e) => {
                    // o primeiro Esc limpa a busca; com ela vazia, fecha a lista
                    if (e.key === 'Escape' && busca !== '') {
                      e.stopPropagation()
                      e.nativeEvent.stopImmediatePropagation()
                      setBusca('')
                    }
                  }}
                />
                {busca.trim() ? (
                  <>
                    <span className="kb__buscar-n" title="Itens encontrados">
                      {achados.length}
                    </span>
                    <button
                      type="button"
                      className="kb__buscar-x"
                      aria-label="Limpar a busca"
                      title="Limpar a busca (Esc)"
                      onClick={() => setBusca('')}
                    >
                      <i className="fas fa-times" aria-hidden />
                    </button>
                  </>
                ) : null}
              </div>
              {achados.length === 0 ? (
                <p className="kb__naomapeados-vazio">Nenhum item com “{busca.trim()}”.</p>
              ) : null}
              <ul>
                {achados.map((item) => (
                  <li key={item.id} className="kb__naomapeados-linha">
                    <button
                      type="button"
                      className="kb__naomapeados-codigo"
                      title={`Abrir ${item.code} para editar`}
                      onClick={() => {
                        setAberta(false)
                        navigate(`/kanbans/${boardId}/items/${item.id}`)
                      }}
                    >
                      {item.code}
                    </button>
                    <span className="kb__naomapeados-titulo" title={item.title}>
                      {item.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
